import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

/** A configured metric: which primitive + its params + a human label. */
export interface MetricSpec {
  primitive: string;
  label: string;
  params: Record<string, string>;
}

/** A computed metric returned by compute_scoreboard_metrics. */
export interface ComputedMetric {
  primitive: string;
  label: string;
  value: number | null;
  unit: string;
  denom: number | null;
  pct: number | null;
  detail: Record<string, unknown> | null;
  params: Record<string, string>;
  why?: string | null;
}

/** Saved per-case scoreboard config (the chosen metric specs). */
export function useScoreboardConfig(caseId: string | undefined) {
  return useQuery<MetricSpec[], Error>({
    queryKey: ["scoreboard-config", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("get_scoreboard_config", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as MetricSpec[]) ?? [];
    },
  });
}

/** Compute the real numbers for a set of specs (deterministic, server-side). */
export function useComputedMetrics(
  caseId: string | undefined,
  specs: MetricSpec[] | undefined,
) {
  return useQuery<ComputedMetric[], Error>({
    queryKey: ["scoreboard-metrics", caseId, specs],
    enabled:
      isSupabaseConfigured() && !!caseId && !!specs && specs.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc(
        "compute_scoreboard_metrics",
        { p_case_id: caseId, p_specs: specs },
      );
      if (error) throw new Error(error.message);
      return (data as ComputedMetric[]) ?? [];
    },
  });
}

/** Persist the chosen specs for a case. */
export function useSaveScoreboardConfig(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<MetricSpec[], Error, MetricSpec[]>({
    mutationFn: async (specs) => {
      const { data, error } = await berserkr().rpc("set_scoreboard_config", {
        p_case_id: caseId,
        p_specs: specs,
      });
      if (error) throw new Error(error.message);
      return (data as MetricSpec[]) ?? specs;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scoreboard-config", caseId] });
      qc.invalidateQueries({ queryKey: ["scoreboard-metrics", caseId] });
    },
  });
}

/**
 * Ask the AI suggester to turn a plain-language interest into 3-4 quantifiable
 * metrics, each already computed against the real corpus (preview values).
 */
export async function suggestMetrics(
  caseId: string,
  interest: string,
): Promise<ComputedMetric[]> {
  const sb = getSupabase();
  if (!sb) throw new Error("Supabase not configured");
  const { data, error } = await sb.functions.invoke("suggest-scoreboard-metrics", {
    body: { case_id: caseId, interest },
  });
  if (error) throw new Error(error.message);
  const payload = data as { metrics?: ComputedMetric[]; error?: string };
  if (payload?.error) throw new Error(payload.error);
  return payload?.metrics ?? [];
}

/** One drilled-down evidence row behind a metric. */
export interface EvidenceItem {
  kind: string;
  title: string;
  subtitle?: string | null;
  snippet?: string | null;
  date?: string | null;
  chunk_id?: string | null;
  extra?: Record<string, unknown> | null;
  evidence?: Array<{
    quote: string;
    chunk_id: string | null;
    source: string | null;
    date: string | null;
  }> | null;
}

export interface MetricEvidence {
  primitive: string;
  label: string;
  items: EvidenceItem[];
}

/** Drill-down: the actual rows a metric's number is built from. */
export function useMetricEvidence(
  caseId: string | undefined,
  spec: MetricSpec | null,
) {
  return useQuery<MetricEvidence, Error>({
    queryKey: ["metric-evidence", caseId, spec],
    enabled: isSupabaseConfigured() && !!caseId && !!spec,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("scoreboard_metric_evidence", {
        p_case_id: caseId,
        p_spec: spec,
      });
      if (error) throw new Error(error.message);
      return (data as MetricEvidence) ?? { primitive: spec!.primitive, label: spec!.label, items: [] };
    },
  });
}
